import {
  Button,
  ChevronDownIcon,
  DownloadIcon,
  DropdownMenu,
  Flex,
  SimpleSpinner,
  UpgradeBadge,
} from '@buffer-mono/popcorn'

import type { JSX } from 'react'

export interface ExportDropdownProps {
  showExport: boolean
  showUpgradePath: boolean
  isExporting: boolean
  isLongRunning: boolean
  onSelect: (format: 'csv' | 'markdown' | 'pdf') => void
  onDownloadSnapshots: () => void
  canDownloadSnapshots: boolean
  isPreparingSnapshots: boolean
}

export function ExportDropdown({
  showExport,
  showUpgradePath,
  isExporting,
  isLongRunning,
  onSelect,
  onDownloadSnapshots,
  canDownloadSnapshots,
  isPreparingSnapshots,
}: ExportDropdownProps): JSX.Element | null {
  if (!showExport) return null

  if (isExporting || isPreparingSnapshots) {
    return (
      <Button
        size="medium"
        variant="secondary"
        disabled
        aria-live="polite"
        data-testid="export-pending-status"
      >
        <SimpleSpinner />
        {isPreparingSnapshots
          ? 'Preparing images…'
          : isLongRunning
            ? "Exporting… it'll download automatically"
            : 'Exporting…'}
      </Button>
    )
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenu.Trigger>
        <Button size="medium" variant="secondary">
          <DownloadIcon />
          Export
          <ChevronDownIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        <DropdownMenu.Item onSelect={() => onSelect('csv')}>
          <Flex align="center" gap="xs">
            Export as CSV
            {showUpgradePath && (
              <UpgradeBadge size="small" aria-hidden="true" />
            )}
          </Flex>
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => onSelect('markdown')}>
          <Flex align="center" gap="xs">
            Export as Markdown
            {showUpgradePath && (
              <UpgradeBadge size="small" aria-hidden="true" />
            )}
          </Flex>
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => onSelect('pdf')}>
          <Flex align="center" gap="xs">
            Export as PDF
            {showUpgradePath && (
              <UpgradeBadge size="small" aria-hidden="true" />
            )}
          </Flex>
        </DropdownMenu.Item>
        <DropdownMenu.Item
          disabled={!canDownloadSnapshots}
          onSelect={onDownloadSnapshots}
        >
          Download Snapshots
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
